import React, { useEffect, useState } from 'react';
import { serviceStats } from '../mock/mockData';
import { ChevronLeft, ChevronRight, Sparkles, Pause, Play } from 'lucide-react';

const COLUMNS = [
  { key: 'projects',     label: 'Projects Delivered' },
  { key: 'clients',      label: 'Active Clients' },
  { key: 'hoursYr',      label: 'Hours Served Yearly' },
  { key: 'years',        label: 'Years Trusted' },
  { key: 'continents',   label: 'Continents Served' },
  { key: 'satisfaction', label: 'Client Satisfaction' }
];

const RotatingStatsBar = ({ intervalMs = 4500 }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const total = serviceStats.length;

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setIdx(prev => (prev + 1) % total);
      setAnimKey(k => k + 1);
    }, intervalMs);
    return () => clearInterval(t);
  }, [paused, intervalMs, total]);

  const cur = serviceStats[idx];
  const go = (delta) => {
    setIdx(prev => (prev + delta + total) % total);
    setAnimKey(k => k + 1);
  };

  return (
    <div className="relative bg-gradient-to-r from-gray-700 via-gray-600 to-gray-700 text-white overflow-hidden" data-testid="rotating-stats-bar">
      {/* subtle pattern */}
      <div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(rgba(255,255,255,0.4)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.4)_1px,transparent_1px)] [background-size:40px_40px] pointer-events-none"></div>
      <div className="relative max-w-[1400px] mx-auto px-6 py-5">
        {/* service label header */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-orange-300" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-green-100 font-bold">Live impact across</span>
            <span
              key={`label-${animKey}`}
              data-testid="stats-current-label"
              className="text-white font-black text-sm md:text-base bg-orange-500 px-3 py-1 rounded animate-fadeSlide"
            >
              {cur.label}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => go(-1)} data-testid="stats-prev" aria-label="Previous service" className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setPaused(p => !p)} data-testid="stats-pause" aria-label={paused ? 'Play' : 'Pause'} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
              {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
            </button>
            <button onClick={() => go(1)} data-testid="stats-next" aria-label="Next service" className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"><ChevronRight className="w-4 h-4" /></button>
            <div className="text-[10px] text-green-100/80 font-bold tabular-nums ml-1">{idx + 1}/{total}</div>
          </div>
        </div>

        {/* stat columns */}
        <div key={animKey} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-center">
          {COLUMNS.map(col => (
            <div key={col.key} className="animate-fadeSlide" data-testid={`stat-${col.key}`}>
              <div className="text-2xl md:text-3xl lg:text-4xl font-black tabular-nums">{cur[col.key]}</div>
              <div className="text-[10px] md:text-[11px] uppercase tracking-wider mt-1 text-green-100">{col.label}</div>
            </div>
          ))}
        </div>

        {/* progress dots */}
        <div className="mt-4 flex items-center justify-center gap-1.5" role="tablist">
          {serviceStats.map((s, i) => (
            <button
              key={s.id}
              onClick={() => { setIdx(i); setAnimKey(k => k + 1); }}
              aria-label={`Show ${s.label}`}
              aria-selected={i === idx}
              data-testid={`stats-dot-${s.id}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-orange-400' : 'w-1.5 bg-white/30 hover:bg-white/50'}`}
            />
          ))}
        </div>
      </div>
      <style>{`
        @keyframes fadeSlide {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeSlide { animation: fadeSlide 500ms ease-out both; }
      `}</style>
    </div>
  );
};

export default RotatingStatsBar;
